// navGuard.tsx - v11.8.4.5
// T2-only navigation guard (no router migration). A page (e.g. SettingsPage) registers an
// interceptor; the Shell calls attempt(path) before navigating. If the interceptor returns
// true, it has handled the navigation (e.g. shown an unsaved-changes dialog) and the Shell
// must NOT navigate. Returns false -> navigate normally.
import { createContext, useContext, useRef, ReactNode } from 'react';

export type NavInterceptor = (path: string) => boolean;

interface NavGuard {
  register: (fn: NavInterceptor | null) => void;
  attempt: (path: string) => boolean;
}

const NavGuardContext = createContext<NavGuard>({
  register: () => {},
  attempt: () => false,
});

export function NavGuardProvider({ children }: { children: ReactNode }) {
  const interceptor = useRef<NavInterceptor | null>(null);
  const api = useRef<NavGuard>({
    register: (fn) => {
      interceptor.current = fn;
    },
    attempt: (path) => (interceptor.current ? interceptor.current(path) : false),
  }).current;

  return <NavGuardContext.Provider value={api}>{children}</NavGuardContext.Provider>;
}

export function useNavGuard(): NavGuard {
  return useContext(NavGuardContext);
}
